import { useMutation, useQuery } from "@apollo/client";
import {
  ArrowDownTrayIcon,
  CameraIcon,
  DevicePhoneMobileIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  FilmIcon,
  GlobeAltIcon,
  HandThumbUpIcon,
  MagnifyingGlassIcon,
  NoSymbolIcon,
  PauseIcon,
  PencilSquareIcon,
  PhotoIcon,
  PlayIcon,
  RocketLaunchIcon,
  ShieldExclamationIcon,
  TrashIcon,
  UserCircleIcon,
  UserIcon,
  UserMinusIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { IconButton, Tooltip, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { useNavigate } from "react-router-dom";

import ActionButton from "../../components/ActionButton/ActionButton";
import EmptyState from "../../components/EmptyState/EmptyState";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Loader from "../../components/Loader/Loader";
import Page from "../../components/Page/Page";
import Badge from "../../components/Styled/Badge";
import { Col, Row } from "../../components/Styled/Container";
import {
  MediaActionLabel,
  MediaActionRow,
  MediaActions,
  MediaBadges,
  MediaCard,
  MediaFooter,
  MediaFrame,
  MediaGrid,
  MediaImage,
  MediaMeta,
  MediaTop,
} from "../../components/Styled/Media";
import Text from "../../components/Styled/Text";
import TextInput from "../../components/TextInput/TextInput";
import { useDismissableSnackbar } from "../../hooks/useDismissableSnackbar";

import {
  Reel,
  ReelRejectReason,
  ReelsApprovalResult,
  ReelsApprovalSort,
} from "../../graphql/graphql";
import {
  APPROVE_REEL,
  DELETE_REEL,
  MARK_REEL_FOR_HLS_CONVERT,
  PROMOTE_REEL,
  SET_REEL_DESCRIPTION,
  SET_REEL_EXPLICIT,
  UPLOAD_REEL_THUMBNAIL,
} from "../../graphql/mutations-gql";
import { REELS_APPROVAL, USER_APPROVED_REELS } from "../../graphql/queries-gql";
import UserReelHistory from './UserReelHistory';

import {
  CallToActionContainer,
  CloseButton,
  ConfirmButton,
  ConvertHlsButton,
  DeleteButton,
  DeleteConfirmationButtons,
  DeleteConfirmationContent,
  DeleteConfirmationOverlay,
  DescriptionInput,
  LatestReelCard,
  ModalContent,
  ModalOverlay,
  PlayPauseButton,
  ProfileThumb,
  ReelInfo,
  ReelVideo,
  SectionHeader,
  SectionTitle,
  Tag,
  TagsContainer,
  ThumbnailActionButton,
  ThumbnailActions,
  ThumbnailButton,
  ThumbnailPreviewContainer,
  ThumbnailPreviewImage,
  ThumbnailPreviewRow,
  TimeDisplay,
  VideoBadge,
  VideoContainer,
  VideoControls,
  VideoControlsRow,
  VideoProgress,
  VideoTimeline,
} from "./styled";

const REJECT_ACTIONS: Array<{
  reason: ReelRejectReason;
  label: string;
  icon: JSX.Element;
}> = [
  {
    reason: ReelRejectReason.Inappropriate,
    label: "Reject: inappropriate",
    icon: <NoSymbolIcon />,
  },
  {
    reason: ReelRejectReason.DoesNotShowYourself,
    label: "Reject: does not show the user",
    icon: <UserMinusIcon />,
  },
  {
    reason: ReelRejectReason.DoesNotMatchProfile,
    label: "Reject: does not match profile",
    icon: <UserCircleIcon />,
  },
  {
    reason: ReelRejectReason.PoorQuality,
    label: "Reject: poor quality",
    icon: <TrashIcon />,
  },
  {
    reason: ReelRejectReason.StillImagesNotAllowed,
    label: "Reject: still images",
    icon: <PhotoIcon />,
  },
  {
    reason: ReelRejectReason.Duplicate,
    label: "Reject: duplicate",
    icon: <DocumentDuplicateIcon />,
  },
  {
    reason: ReelRejectReason.NotPortraitFormat,
    label: "Reject: wrong orientation, not portrait",
    icon: <DevicePhoneMobileIcon style={{ transform: "rotate(90deg)" }} />,
  },
  {
    reason: ReelRejectReason.Underage,
    label: "Reject: includes underage persons",
    icon: <ExclamationTriangleIcon />,
  },
];

const videoControlSx = {
  color: "white",
  padding: "4px",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
};

const Reels = () => {
  const { data, loading, error, refetch } = useQuery<{
    reelsApproval: ReelsApprovalResult;
  }>(REELS_APPROVAL, {
    variables: {
      args: {
        sort: ReelsApprovalSort.Newest,
      },
    },
    onCompleted: (data) => {
      console.log("fetchReels onCompleted", JSON.stringify(data, null, 2));
    },
  });
  const theme = useTheme();
  const [approveReel] = useMutation(APPROVE_REEL);
  const [deleteReel] = useMutation(DELETE_REEL);
  const [promoteReel] = useMutation(PROMOTE_REEL);
  const [setReelExplicit] = useMutation(SET_REEL_EXPLICIT);
  const [setReelDescription, { loading: savingDescription }] = useMutation(
    SET_REEL_DESCRIPTION
  );
  const [markReelForHlsConvert, { loading: markingForHls }] = useMutation(
    MARK_REEL_FOR_HLS_CONVERT
  );
  const { showSnackbar } = useDismissableSnackbar();
  const navigate = useNavigate();
  const [selectedReel, setSelectedReel] = useState<Reel | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [reelToDelete, setReelToDelete] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // Tracks reel ids the admin has clicked "Convert to HLS" on during this
  // session. The mutation itself is near-instant (just sets markedForHLS),
  // but the actual HLS transcode takes minutes (next backfill run → S3
  // copy → MediaConvert job). Rather than refetching the query (which
  // wouldn't change anything until hlsReady flips much later), we just
  // remember the click locally and disable/relabel the button so the
  // admin gets immediate feedback and can't double-trigger.
  const [markedForHlsIds, setMarkedForHlsIds] = useState<Set<string>>(
    new Set()
  );
  const [uploadReelThumbnail, { loading: uploadingThumbnail }] = useMutation(
    UPLOAD_REEL_THUMBNAIL
  );
  const [capturedFrame, setCapturedFrame] = useState<Blob | null>(null);
  const [capturedFrameUrl, setCapturedFrameUrl] = useState<string | null>(null);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState("");

  const hlsRef = useRef<Hls | null>(null);

  // Attach hls.js for HLS streams. Browsers other than Safari can't play
  // .m3u8 natively, so hls.js handles manifest parsing and segment fetching
  // via MediaSource Extensions. Safari plays HLS natively so we skip hls.js.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !selectedReel?.url) return;

    const url = selectedReel.url;
    const isHls = url.endsWith(".m3u8");

    // Cleanup any previous hls.js instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    // Try to autoplay with sound. Browsers may block unmuted autoplay
    // if the user hasn't interacted with the page yet. In that case
    // fall back to muted autoplay so the video at least starts, and
    // the admin can unmute manually.
    const tryPlay = () => {
      video.play().catch(() => {
        video.muted = true;
        video.play().catch(() => {});
      });
    };

    if (isHls && Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, tryPlay);
      hlsRef.current = hls;
    } else {
      // Native playback (MP4, or Safari which handles HLS natively)
      video.src = url;
      video.addEventListener("loadedmetadata", tryPlay, { once: true });
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [selectedReel]);

  const reels: Reel[] = data?.reelsApproval.reels || [];
  const searchedReels: Reel[] = data?.reelsApproval.searchedReels || [];

  const handleDelete = (reelId: string) => {
    deleteReel({
      variables: {
        reelId,
      },
      onCompleted: (data) => {
        console.log("deleteReel onCompleted", data);
      },
      onError: (error) => {
        console.log("deleteReel onError", error);
      },
      refetchQueries: [REELS_APPROVAL, USER_APPROVED_REELS],
    });
  };

  const handleApprove = (
    value: boolean,
    reelId: string,
    rejectReason: ReelRejectReason,
    explicit: boolean,
    matchVideo: boolean
  ) => {
    approveReel({
      variables: {
        reelId,
        value,
        rejectReason,
        explicit,
        matchVideo,
      },
      onCompleted: (data) => {
        console.log("approveReel onCompleted", data);
      },
      onError: (error) => {
        console.log("approveReel onError", error);
      },
      refetchQueries: [REELS_APPROVAL, USER_APPROVED_REELS],
    });
  };

  // const showVideo = (url: string) => {
  //   window.open(url, "_blank");
  // };

  const handleReelClick = (reel: Reel) => {
    setSelectedReel(reel);
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  const handleCloseModal = () => {
    setSelectedReel(null);
    setCapturedFrame(null);
    setIsEditingDescription(false);
    setDescriptionDraft("");
    if (capturedFrameUrl) {
      URL.revokeObjectURL(capturedFrameUrl);
      setCapturedFrameUrl(null);
    }
  };

  const handleStartEditDescription = () => {
    setDescriptionDraft(selectedReel?.description ?? "");
    setIsEditingDescription(true);
  };

  const handleCancelEditDescription = () => {
    setIsEditingDescription(false);
    setDescriptionDraft("");
  };

  const handleSaveDescription = () => {
    if (!selectedReel) return;
    const newDescription = descriptionDraft;
    setReelDescription({
      variables: {
        reelId: selectedReel._id,
        description: newDescription,
      },
      onCompleted: () => {
        setSelectedReel((prev) =>
          prev && prev._id === selectedReel._id
            ? { ...prev, description: newDescription }
            : prev
        );
        setIsEditingDescription(false);
        setDescriptionDraft("");
        showSnackbar("Description updated", "success");
      },
      onError: (error) => {
        showSnackbar("Failed to update description. " + error.message, "error");
      },
      refetchQueries: [REELS_APPROVAL],
    });
  };

  const handleDeleteClick = (reelId: string) => {
    setReelToDelete(reelId);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (reelToDelete) {
      handleDelete(reelToDelete);
      setShowDeleteConfirm(false);
      setReelToDelete(null);
      handleCloseModal();
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setReelToDelete(null);
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (videoRef.current) {
      const timeline = e.currentTarget;
      const rect = timeline.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = pos * duration;
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleCaptureFrame = () => {
    const video = videoRef.current;
    if (!video) return;

    video.pause();
    setIsPlaying(false);

    const canvas = document.createElement("canvas");
    const maxWidth = 720;
    const scale = Math.min(1, maxWidth / video.videoWidth);
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        // Revoke any previous preview URL before creating a new one
        setCapturedFrameUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
        setCapturedFrame(blob);
      },
      'image/jpeg',
      0.85
    );
  };

  const handleConfirmThumbnail = () => {
    if (!selectedReel || !capturedFrame) return;

    uploadReelThumbnail({
      variables: {
        reelId: selectedReel._id,
        contentType: 'image/jpeg',
        contentLength: String(capturedFrame.size),
        fileName: 'thumbnail.jpeg',
      },
      onCompleted: async (data) => {
        const uploadPostInfo = data.uploadReelThumbnail?.uploadPostInfo;
        if (!uploadPostInfo) {
          showSnackbar('Failed to update thumbnail: missing upload info.', 'error');
          return;
        }

        try {
          const formData = new FormData();
          for (const field of uploadPostInfo.fields ?? []) {
            if (field) formData.append(field.key, field.value);
          }
          formData.append('file', capturedFrame);

          const response = await fetch(uploadPostInfo.postUrl, {
            method: 'POST',
            body: formData,
          });

          if (!response.ok) {
            throw new Error(`S3 upload failed with status ${response.status}`);
          }

          showSnackbar('Thumbnail updated successfully!', 'success');
          setCapturedFrame(null);
          if (capturedFrameUrl) {
            URL.revokeObjectURL(capturedFrameUrl);
            setCapturedFrameUrl(null);
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Unknown error';
          showSnackbar('Failed to upload thumbnail to S3. ' + message, 'error');
        }
      },
      onError: (error) => {
        showSnackbar('Failed to update thumbnail. ' + error.message, 'error');
      },
      refetchQueries: [REELS_APPROVAL],
    });
  };

  const handleCancelThumbnail = () => {
    setCapturedFrame(null);
    if (capturedFrameUrl) {
      URL.revokeObjectURL(capturedFrameUrl);
      setCapturedFrameUrl(null);
    }
  };

  const handleDownload = (url: string) => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
    window.open(url, "_blank");
  };

  const handleMarkForHlsConvert = (reelId: string) => {
    console.log("markReelForHlsConvert: firing mutation", { reelId });
    markReelForHlsConvert({
      variables: {
        reelId,
      },
      onCompleted: (data) => {
        console.log(
          "markReelForHlsConvert onCompleted",
          JSON.stringify(data, null, 2)
        );
        setMarkedForHlsIds((prev) => {
          const next = new Set(prev);
          next.add(reelId);
          return next;
        });
        showSnackbar(
          "Reel marked for HLS backfill. Will be picked up on the next backfill run.",
          "success"
        );
      },
      onError: (error) => {
        console.error("markReelForHlsConvert onError", {
          message: error.message,
          graphQLErrors: error.graphQLErrors,
          networkError: error.networkError,
        });
        showSnackbar("Failed to mark reel for HLS. " + error.message, "error");
      },
    });
  };

  const handleToggleExplicit = (reel: Reel) => {
    const newValue = !reel.explicit;
    setReelExplicit({
      variables: {
        reelId: reel._id,
        explicit: newValue,
      },
      onCompleted: () => {
        setSelectedReel((prev) =>
          prev && prev._id === reel._id ? { ...prev, explicit: newValue } : prev
        );
        showSnackbar(
          newValue ? "Reel marked as explicit" : "Reel unmarked as explicit",
          "success"
        );
      },
      onError: (error) => {
        showSnackbar("Failed to update explicit flag. " + error.message, "error");
      },
      refetchQueries: [REELS_APPROVAL],
    });
  };

  const handlePromote = (reelId: string) => {
    promoteReel({
      variables: {
        reelId,
      },
      onCompleted: (data) => {
        console.log("promoteReel onCompleted", data);
        showSnackbar("Reel promoted successfully!", "success");
      },
      onError: (error) => {
        console.log("promoteReel onError", error);
        showSnackbar("Failed to promote reel. " + error.message, "error");
      },
      refetchQueries: [REELS_APPROVAL],
    });
  };

  if (loading) {
    return (
      <Page title="Reels">
        <Loader />
      </Page>
    );
  }
  if (error) {
    return (
      <Page title="Reels">
        <ErrorAlert error={error} />
      </Page>
    );
  }

  return (
    <Page
      title="Reels"
      description="Hover a reel to approve or reject it. Click it to play and edit."
      badge={
        reels.length > 0 && (
          <Badge $tone="primary">{reels.length} to approve</Badge>
        )
      }
    >
      {reels.length === 0 ? (
        <EmptyState
          Icon={FilmIcon}
          title="No reels waiting"
          description="New uploads show up here for approval."
        />
      ) : (
        <MediaGrid $min={290}>
          {reels.map((reel) => (
            <MediaCard key={reel._id}>
              <MediaFrame $ratio="9 / 16" onClick={() => handleReelClick(reel)}>
                <MediaImage src={reel.thumbnailUrl ?? ""} alt="" loading="lazy" />
                <MediaTop>
                  {/* Uploader's profile photo, grows on hover to compare faces */}
                  {reel.profilePhotoUrl ? (
                    <Tooltip title="Open user" placement="right">
                      <ProfileThumb
                        src={reel.profilePhotoUrl}
                        alt=""
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/user/${reel.byUserId}`);
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <ActionButton
                      label="Open user"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/user/${reel.byUserId}`);
                      }}
                    >
                      <UserIcon />
                    </ActionButton>
                  )}
                  <MediaBadges $align="end">
                    <Badge $solid>
                      {reel.matchVideo ? "Match video" : "General video"}
                    </Badge>
                    {reel.hlsReady && (
                      <Badge $solid $tone="purple">
                        HLS
                      </Badge>
                    )}
                    {reel.explicit && (
                      <Badge $solid $tone="red">
                        18+
                      </Badge>
                    )}
                  </MediaBadges>
                </MediaTop>

                <MediaActions onClick={(e) => e.stopPropagation()}>
                  <MediaActionRow>
                    <ActionButton
                      label="Approve as general video"
                      tone="warn"
                      onClick={() =>
                        handleApprove(
                          true,
                          reel._id,
                          ReelRejectReason.None,
                          false,
                          false
                        )
                      }
                    >
                      <GlobeAltIcon />
                    </ActionButton>
                    <ActionButton
                      label="Approve"
                      tone="approve"
                      size={46}
                      onClick={() =>
                        handleApprove(
                          true,
                          reel._id,
                          ReelRejectReason.None,
                          false,
                          reel.matchVideo
                        )
                      }
                    >
                      <HandThumbUpIcon />
                    </ActionButton>
                    <ActionButton
                      label="Approve as explicit (18+)"
                      tone="approve"
                      onClick={() =>
                        handleApprove(
                          true,
                          reel._id,
                          ReelRejectReason.None,
                          true,
                          reel.matchVideo
                        )
                      }
                    >
                      <ShieldExclamationIcon />
                    </ActionButton>
                  </MediaActionRow>

                  <MediaActionLabel>Reject as</MediaActionLabel>
                  <MediaActionRow>
                    {REJECT_ACTIONS.map((action) => (
                      <ActionButton
                        key={action.reason}
                        label={action.label}
                        tone="reject"
                        size={34}
                        onClick={() =>
                          handleApprove(
                            false,
                            reel._id,
                            action.reason,
                            false,
                            false
                          )
                        }
                      >
                        {action.icon}
                      </ActionButton>
                    ))}
                  </MediaActionRow>
                </MediaActions>
              </MediaFrame>

              {((reel.tags && reel.tags.length > 0) ||
                reel.description ||
                reel.callToAction) && (
                <MediaFooter>
                  {reel.tags && reel.tags.length > 0 && (
                    <TagsContainer>
                      {reel.tags.map((tag) => (
                        <Tag key={tag}>#{tag}</Tag>
                      ))}
                    </TagsContainer>
                  )}
                  {reel.description && <MediaMeta>{reel.description}</MediaMeta>}
                  {reel.callToAction && (
                    <CallToActionContainer>
                      <Text size={13} weight={600}>
                        Call to action
                      </Text>
                      <Text size={13}>{reel.callToAction}</Text>
                      <MediaMeta>
                        Type: {reel.callToActionType} · Delay:{" "}
                        {reel.callToActionButtonDelayMs}ms
                      </MediaMeta>
                    </CallToActionContainer>
                  )}
                </MediaFooter>
              )}
              <UserReelHistory
                userId={reel.byUserId}
                currentReelId={reel._id}
                onReelClick={handleReelClick}
              />
            </MediaCard>
          ))}
        </MediaGrid>
      )}

      <SectionHeader>
        <SectionTitle>Latest reels</SectionTitle>
        <div style={{ width: 300, maxWidth: "100%" }}>
          <TextInput
            placeHolder="Search by reel ID or user ID"
            icon={<MagnifyingGlassIcon width={16} />}
            onSubmit={(value) =>
              refetch({
                args: {
                  sort: ReelsApprovalSort.Newest,
                  search: value || undefined,
                },
              })
            }
            allowEmpty
          />
        </div>
      </SectionHeader>

      <MediaGrid $min={170} $gap={12}>
        {searchedReels.map((reel) => (
          <LatestReelCard key={reel._id} onClick={() => handleReelClick(reel)}>
            <MediaFrame $ratio="9 / 16">
              <MediaImage src={reel.thumbnailUrl ?? ""} alt="" loading="lazy" />
              <MediaTop>
                <span />
                <MediaBadges $align="end">
                  <Badge $solid>{reel.matchVideo ? "Match" : "General"}</Badge>
                  {reel.hlsReady && (
                    <Badge $solid $tone="purple">
                      HLS
                    </Badge>
                  )}
                  {reel.explicit && (
                    <Badge $solid $tone="red">
                      18+
                    </Badge>
                  )}
                </MediaBadges>
              </MediaTop>
              {!reel.hlsReady &&
                (markedForHlsIds.has(reel._id) ? (
                  <ConvertHlsButton disabled>Processing...</ConvertHlsButton>
                ) : (
                  <ConvertHlsButton
                    disabled={markingForHls}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkForHlsConvert(reel._id);
                    }}
                  >
                    Convert to HLS
                  </ConvertHlsButton>
                ))}
            </MediaFrame>
          </LatestReelCard>
        ))}
      </MediaGrid>

      {selectedReel && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={handleCloseModal}>
              <XMarkIcon width={24} />
            </CloseButton>

            <DeleteButton onClick={() => handleDeleteClick(selectedReel._id)}>
              <TrashIcon />
              Delete reel
            </DeleteButton>

            <VideoContainer>
              <ReelVideo
                ref={videoRef}
                onClick={handlePlayPause}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                playsInline
                loop
              />
              <VideoControls>
                <VideoTimeline onClick={handleTimelineClick}>
                  <VideoProgress progress={(currentTime / duration) * 100} />
                </VideoTimeline>
                <VideoControlsRow>
                  <PlayPauseButton onClick={handlePlayPause}>
                    {isPlaying ? <PauseIcon /> : <PlayIcon />}
                  </PlayPauseButton>
                  <TimeDisplay>
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </TimeDisplay>
                  <Tooltip title="Open video file" placement="top">
                    <IconButton
                      onClick={() => handleDownload(selectedReel.url ?? "")}
                      sx={videoControlSx}
                    >
                      <ArrowDownTrayIcon width={20} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Promote reel" placement="top">
                    <IconButton
                      onClick={() => handlePromote(selectedReel._id)}
                      sx={videoControlSx}
                    >
                      <RocketLaunchIcon width={20} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      selectedReel.explicit
                        ? "Unmark as explicit"
                        : "Mark as explicit"
                    }
                    placement="top"
                  >
                    <IconButton
                      onClick={() => handleToggleExplicit(selectedReel)}
                      sx={{
                        ...videoControlSx,
                        color: selectedReel.explicit ? "#ff6b6b" : "white",
                      }}
                    >
                      <ShieldExclamationIcon width={20} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Capture frame as thumbnail" placement="top">
                    <ThumbnailButton onClick={handleCaptureFrame}>
                      <CameraIcon width={20} />
                    </ThumbnailButton>
                  </Tooltip>
                  <Tooltip title="Reject as inappropriate" placement="top">
                    <IconButton
                      onClick={() => {
                        handleApprove(
                          false,
                          selectedReel._id,
                          ReelRejectReason.Inappropriate,
                          false,
                          false
                        );
                        handleCloseModal();
                      }}
                      sx={{ ...videoControlSx, color: "#ff6b6b" }}
                    >
                      <NoSymbolIcon width={20} />
                    </IconButton>
                  </Tooltip>
                </VideoControlsRow>
              </VideoControls>
              <VideoBadge>
                <Badge $solid>
                  {selectedReel.matchVideo ? "Match video" : "General video"}
                </Badge>
                {selectedReel.hlsReady && (
                  <Badge $solid $tone="purple">
                    HLS
                  </Badge>
                )}
              </VideoBadge>
            </VideoContainer>

            {capturedFrame && capturedFrameUrl && (
              <ThumbnailPreviewContainer>
                <Text size={14} weight={600} style={{ marginBottom: "8px" }}>
                  Set as thumbnail?
                </Text>
                <ThumbnailPreviewRow>
                  <ThumbnailPreviewImage src={capturedFrameUrl} alt="Captured frame" />
                  <ThumbnailActions>
                    <ThumbnailActionButton
                      variant="confirm"
                      onClick={handleConfirmThumbnail}
                      disabled={uploadingThumbnail}
                    >
                      {uploadingThumbnail ? "Uploading..." : "Set Thumbnail"}
                    </ThumbnailActionButton>
                    <ThumbnailActionButton
                      variant="cancel"
                      onClick={handleCancelThumbnail}
                      disabled={uploadingThumbnail}
                    >
                      Cancel
                    </ThumbnailActionButton>
                  </ThumbnailActions>
                </ThumbnailPreviewRow>
              </ThumbnailPreviewContainer>
            )}

            <ReelInfo>
              <MediaMeta>
                Reel{" "}
                <span style={{ fontFamily: theme.monoFontFamily }}>
                  {selectedReel._id}
                </span>
              </MediaMeta>
              <MediaMeta>
                by{" "}
                <span style={{ fontFamily: theme.monoFontFamily }}>
                  {selectedReel.byUserId}
                </span>
              </MediaMeta>

              {selectedReel.tags && selectedReel.tags.length > 0 && (
                <TagsContainer>
                  {selectedReel.tags.map((tag) => (
                    <Tag key={tag}>#{tag}</Tag>
                  ))}
                </TagsContainer>
              )}

              <div style={{ marginTop: "4px" }}>
                {isEditingDescription ? (
                  <Col padding={0}>
                    <DescriptionInput
                      value={descriptionDraft}
                      onChange={(e) => setDescriptionDraft(e.target.value)}
                      rows={3}
                      autoFocus
                    />
                    <Row style={{ gap: "8px", marginTop: "8px" }}>
                      <ThumbnailActionButton
                        variant="confirm"
                        onClick={handleSaveDescription}
                        disabled={savingDescription}
                        style={{ width: "auto", padding: "6px 14px" }}
                      >
                        {savingDescription ? "Saving..." : "Save"}
                      </ThumbnailActionButton>
                      <ThumbnailActionButton
                        variant="cancel"
                        onClick={handleCancelEditDescription}
                        disabled={savingDescription}
                        style={{ width: "auto", padding: "6px 14px" }}
                      >
                        Cancel
                      </ThumbnailActionButton>
                    </Row>
                  </Col>
                ) : (
                  <Row style={{ gap: "8px", alignItems: "flex-start" }}>
                    {selectedReel.description ? (
                      <Text size={14} style={{ flex: 1 }}>
                        {selectedReel.description}
                      </Text>
                    ) : (
                      <Text
                        size={14}
                        color={theme.mutedTextColor}
                        style={{ flex: 1, fontStyle: "italic" }}
                      >
                        No description
                      </Text>
                    )}
                    <IconButton
                      onClick={handleStartEditDescription}
                      sx={{
                        padding: "4px",
                        color: theme.secondaryTextColor,
                      }}
                      title="Edit description"
                    >
                      <PencilSquareIcon width={18} />
                    </IconButton>
                  </Row>
                )}
              </div>
            </ReelInfo>
          </ModalContent>
        </ModalOverlay>
      )}

      {showDeleteConfirm && (
        <DeleteConfirmationOverlay onClick={handleCancelDelete}>
          <DeleteConfirmationContent onClick={(e) => e.stopPropagation()}>
            <Text size={16} weight={600}>
              Delete reel
            </Text>
            <Text size={14} color={theme.secondaryTextColor}>
              Are you sure you want to delete this reel? This action cannot be
              undone.
            </Text>
            <DeleteConfirmationButtons>
              <ConfirmButton variant="cancel" onClick={handleCancelDelete}>
                Cancel
              </ConfirmButton>
              <ConfirmButton variant="danger" onClick={handleConfirmDelete}>
                Delete
              </ConfirmButton>
            </DeleteConfirmationButtons>
          </DeleteConfirmationContent>
        </DeleteConfirmationOverlay>
      )}
    </Page>
  );
};
export default Reels;
